import {ScreenFullIcon} from '@primer/octicons-react'
import {Dialog, IconButton} from '@primer/react'
import {useState, useRef, useEffect} from 'react'
import {MarkdownRenderer} from '@github-ui/copilot-markdown'
import styles from './PlanApprovalDisplay.module.css'
import {UserChoiceCard} from './UserChoiceCard'
import {ChoiceButton, ChoiceButtonInput, ChoiceButtonGroup} from './ChoiceButton'
import {useLatestAgentModel} from '../../hooks/use-latest-agent-model'
import {useLatestReasoningEffort} from '../../hooks/use-latest-reasoning-effort'
import {
  useModelSupportedReasoningEfforts,
  useReasoningEffortAutoFallback,
  useSelectModelClearingReasoningEffort,
} from '../../hooks/use-model-supported-reasoning-efforts'
import {ModelPicker} from '../task-view/ModelPicker'
import {ReasoningEffortPicker} from '../task-view/ReasoningEffortPicker'
import {copilotFeatureFlags} from '@github-ui/copilot-feature-flags'
import {useLastSelectedAgent} from '../../hooks/use-last-selected-agent'
import {useModelPickerEligibility} from '../../hooks/use-model-picker-eligibility'
import {stripPlanTags} from '../../utils/plan-tags'

const ACTION_LABELS: Record<string, string> = {
  implement: 'Approve plan and start work',
  autopilot: 'Accept plan and build on autopilot',
  interactive: 'Accept plan and build interactively',
  exit_only: 'Exit plan mode and I will prompt myself',
  autopilot_fleet: 'Accept plan and build on autopilot fleet',
}

const ACTION_DESCRIPTIONS: Partial<Record<string, string>> = {
  implement: 'Copilot will implement the plan end-to-end',
  autopilot: 'Copilot will implement the plan autonomously',
  interactive: 'Copilot will ask for confirmation at each step',
  exit_only: 'You will take over and prompt Copilot yourself',
  autopilot_fleet: 'Run plan across multiple agents in parallel',
}

export interface PlanApprovalDisplayProps {
  /** Markdown summary of the plan */
  summary?: string
  /** Short description shown below the title */
  subtitle?: string
  /** Full plan.md content */
  planContent?: string
  /** Available action IDs */
  actions?: string[]
  /** The recommended action ID */
  recommendedAction?: string
  /** Callback when an action button is clicked */
  onAction?: (action: string) => void
  /** Callback when feedback text is submitted */
  onFeedback?: (feedback: string) => void
  /** Whether action buttons are disabled (e.g. while submitting) */
  disabled?: boolean
  /** When true, hides container chrome and header (for embedding in another container) */
  embedded?: boolean
  /** Controlled dialog open state (for embedded mode where parent controls the expand button) */
  dialogOpen?: boolean
  /** Callback when dialog is closed (for embedded mode) */
  onDialogClose?: () => void
  /** When true, hides the model picker (e.g. for CLI sessions where model is controlled externally) */
  hideModelPicker?: boolean
  /** When provided, controls whether the model picker renders TBB vs PRU billing UI */
  isTokenBasedBillingEnabled?: boolean
}

const DEFAULT_ACTIONS = ['implement', 'autopilot', 'interactive', 'exit_only']

/** Display a plan approval prompt with action choices and feedback input */
export function PlanApprovalDisplay({
  summary,
  subtitle,
  planContent,
  actions = DEFAULT_ACTIONS,
  recommendedAction,
  onAction,
  onFeedback,
  disabled = false,
  embedded = false,
  dialogOpen: controlledDialogOpen,
  onDialogClose,
  hideModelPicker = false,
  isTokenBasedBillingEnabled,
}: PlanApprovalDisplayProps) {
  const cleanSummary = summary ? stripPlanTags(summary).trim() || undefined : undefined
  const cleanPlanContent = planContent ? stripPlanTags(planContent).trim() || undefined : undefined

  const [feedbackValue, setFeedbackValue] = useState('')
  const [showFeedback, setShowFeedback] = useState(false)
  const [internalDialogOpen, setInternalDialogOpen] = useState(false)
  const effectiveRecommended = recommendedAction && actions.includes(recommendedAction) ? recommendedAction : actions[0]

  const [selectedModelId, setSelectedModelId] = useLatestAgentModel()
  const [selectedReasoningEffort, setSelectedReasoningEffort] = useLatestReasoningEffort()
  const supportedReasoningEfforts = useModelSupportedReasoningEfforts(selectedModelId)
  const selectModel = useSelectModelClearingReasoningEffort(
    setSelectedModelId,
    selectedReasoningEffort,
    setSelectedReasoningEffort,
  )
  useReasoningEffortAutoFallback(selectedReasoningEffort, supportedReasoningEfforts, setSelectedReasoningEffort)
  const [selectedAgent] = useLastSelectedAgent()
  const isEligible = useModelPickerEligibility(selectedAgent)
  const shouldShowModelPicker = isEligible && !hideModelPicker

  const isControlled = controlledDialogOpen !== undefined
  const dialogOpen = isControlled ? controlledDialogOpen : internalDialogOpen
  const closeDialog = () => {
    if (isControlled) {
      onDialogClose?.()
    } else {
      setInternalDialogOpen(false)
    }
  }
  const openDialog = () => setInternalDialogOpen(true)

  const handleFeedback = () => {
    if (!onFeedback || !feedbackValue.trim()) return
    onFeedback(feedbackValue.trim())
  }

  return (
    <>
      <UserChoiceCard
        title="Plan ready for review"
        subtitle={subtitle}
        embedded={embedded}
        headerButtons={
          !embedded ? (
            <IconButton
              icon={ScreenFullIcon}
              aria-label="View full plan"
              size="small"
              variant="invisible"
              onClick={openDialog}
            />
          ) : undefined
        }
        previewContent={
          cleanSummary ? (
            <div className={styles.summary}>
              <MarkdownRenderer markdown={cleanSummary} />
            </div>
          ) : undefined
        }
      >
        <ChoiceButtonGroup>
          {actions.map((action, i) => (
            <ChoiceButton
              key={action}
              number={i + 1}
              label={ACTION_LABELS[action] ?? action}
              description={ACTION_DESCRIPTIONS[action]}
              isRecommended={action === effectiveRecommended}
              disabled={disabled}
              onClick={() => onAction?.(action)}
              optionsSlot={
                shouldShowModelPicker && action === effectiveRecommended ? (
                  <>
                    <ModelPicker
                      selectedModelId={selectedModelId}
                      onUpdateModel={selectModel}
                      isTokenBasedBillingEnabled={isTokenBasedBillingEnabled}
                    />
                    {copilotFeatureFlags.codingAgentReasoningEffort && (
                      <ReasoningEffortPicker
                        selectedReasoningEffort={selectedReasoningEffort}
                        onUpdateReasoningEffort={setSelectedReasoningEffort}
                        supportedReasoningEfforts={supportedReasoningEfforts}
                      />
                    )}
                  </>
                ) : undefined
              }
            />
          ))}
          {onFeedback &&
            (showFeedback ? (
              <ChoiceButtonInput
                number={actions.length + 1}
                placeholder="Suggest changes..."
                value={feedbackValue}
                onChange={setFeedbackValue}
                onSubmit={handleFeedback}
              />
            ) : (
              <ChoiceButton
                number={actions.length + 1}
                label="Suggest changes"
                disabled={disabled}
                onClick={() => setShowFeedback(true)}
              />
            ))}
        </ChoiceButtonGroup>
      </UserChoiceCard>

      {dialogOpen && (
        <PlanDialog
          planContent={cleanPlanContent || cleanSummary || ''}
          actions={actions}
          effectiveRecommended={effectiveRecommended}
          feedbackValue={feedbackValue}
          showFeedback={showFeedback}
          onClose={closeDialog}
          onAction={onAction}
          onFeedback={onFeedback}
          onFeedbackChange={setFeedbackValue}
          onShowFeedback={() => setShowFeedback(true)}
          onSubmitFeedback={handleFeedback}
        />
      )}
    </>
  )
}

interface PlanDialogProps {
  planContent: string
  actions: string[]
  effectiveRecommended: string | undefined
  feedbackValue: string
  showFeedback: boolean
  onClose: () => void
  onAction?: (action: string) => void
  onFeedback?: (feedback: string) => void
  onFeedbackChange: (value: string) => void
  onShowFeedback: () => void
  onSubmitFeedback: () => void
}

function PlanDialog({
  planContent,
  actions,
  effectiveRecommended,
  feedbackValue,
  showFeedback,
  onClose,
  onAction,
  onFeedback,
  onFeedbackChange,
  onShowFeedback,
  onSubmitFeedback,
}: PlanDialogProps) {
  const contentRef = useRef<HTMLDivElement>(null)
  const [showChoices, setShowChoices] = useState(true)

  useEffect(() => {
    const el = contentRef.current
    if (!el) return
    // Show inline choices when content is short enough (at most ~2 viewports of scroll)
    setShowChoices(el.scrollHeight <= el.clientHeight * 2)
  }, [])

  return (
    <Dialog onClose={onClose} title="Plan" width="xlarge" className={styles.planDialog}>
      <div ref={contentRef} className={styles.dialogContent}>
        <MarkdownRenderer markdown={planContent} />
      </div>
      {showChoices ? (
        <div className={styles.dialogActions}>
          <ChoiceButtonGroup>
            {actions.map((action, i) => (
              <ChoiceButton
                key={action}
                number={i + 1}
                label={ACTION_LABELS[action] ?? action}
                description={ACTION_DESCRIPTIONS[action]}
                isRecommended={action === effectiveRecommended}
                onClick={() => onAction?.(action)}
              />
            ))}
            {onFeedback &&
              (showFeedback ? (
                <ChoiceButtonInput
                  number={actions.length + 1}
                  placeholder="Suggest changes..."
                  value={feedbackValue}
                  onChange={onFeedbackChange}
                  onSubmit={onSubmitFeedback}
                />
              ) : (
                <ChoiceButton number={actions.length + 1} label="Suggest changes" onClick={onShowFeedback} />
              ))}
          </ChoiceButtonGroup>
        </div>
      ) : null}
    </Dialog>
  )
}
